import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { History, User, Shield } from 'lucide-react';
import API_URL from '../../config/api';

const PAGE_SIZE = 50;

// Etiquetas legibles por tipo de acción — si se agrega un tipo nuevo en el
// backend (logAudit), agregarlo acá para que no se muestre el nombre
// técnico crudo en la tabla.
const ACTION_LABELS = {
  sale_cancelled: 'Venta anulada',
  price_changed: 'Cambio de precio',
  stock_adjusted: 'Ajuste de stock',
  batch_received: 'Lote recibido',
  batch_adjusted: 'Lote ajustado',
  batch_removed: 'Lote retirado',
  // ⭐ Categoría "Conversaciones" (pedido de Angelo, 2026-08-05): esta
  // acción solo aparece cuando se filtra explícitamente por esa categoría
  // -- ver el filtro `category` más abajo y el comentario en el backend
  // (GET /api/pos/audit-log).
  crm_message_sent: 'Mensaje enviado (WhatsApp)'
};

const ACTION_COLORS = {
  sale_cancelled: 'bg-red-100 text-red-700',
  price_changed: 'bg-orange-100 text-orange-700',
  stock_adjusted: 'bg-blue-100 text-blue-700',
  batch_received: 'bg-green-100 text-green-700',
  batch_adjusted: 'bg-blue-100 text-blue-700',
  batch_removed: 'bg-gray-200 text-gray-700',
  crm_message_sent: 'bg-purple-100 text-purple-700'
};

// Convierte el JSON de `detail` en una frase legible, distinta por tipo de
// acción — evita mostrarle al dueño un blob de JSON crudo.
function formatDetail(action, detail) {
  if (!detail) return '';
  switch (action) {
    case 'sale_cancelled':
      return `Motivo: ${detail.reason || '(sin motivo)'}${detail.total != null ? ` · Total: $${Number(detail.total).toFixed(2)}` : ''}`;
    case 'price_changed':
      return `${detail.name || 'Producto'}: $${Number(detail.price_before).toFixed(2)} → $${Number(detail.price_after).toFixed(2)}`;
    case 'stock_adjusted':
      return `${detail.name || 'Producto'}: ${detail.stock_before} → ${detail.stock_after} unidades`;
    case 'batch_received':
      return `${detail.name || 'Producto'}: +${detail.quantity} unidades (lote #${detail.batch_id})`;
    case 'batch_adjusted':
      return `${detail.name || 'Producto'}: lote #${detail.batch_id} de ${detail.quantity_before} a ${detail.quantity_after} (${detail.difference > 0 ? '+' : ''}${detail.difference})`;
    case 'batch_removed':
      return `${detail.name || 'Producto'}: retirado el lote #${detail.batch_id} (-${detail.quantity} unidades)`;
    case 'crm_message_sent':
      return `A ${detail.contactPhone || '(sin teléfono)'}: "${detail.preview || ''}"`;
    default:
      return JSON.stringify(detail);
  }
}

function AuditoriaLog({ token }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState(''); // '' | 'owner' | employee_id
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // ⭐ Categoría (pedido de Angelo, 2026-08-05: "que se refleje en la
  // auditoría, pero SOLO cuando se filtre en Conversaciones"). Default
  // 'general' -- el backend ya excluye crm_message_sent salvo que se pida
  // category=conversaciones explícitamente, ver GET /api/pos/audit-log.
  const [categoryFilter, setCategoryFilter] = useState('general');

  const abortRef = useRef(null);

  useEffect(() => {
    loadEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter, actorFilter, dateFrom, dateTo, categoryFilter]);

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/audit-log/employees`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(res.data || []);
    } catch (e) { /* silencioso: el filtro por empleado simplemente queda vacío */ }
  };

  const load = async ({ reset = false } = {}) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const nextPage = reset ? 1 : page + 1;
    setLoading(true);
    try {
      const params = { page: nextPage, limit: PAGE_SIZE };
      if (actionFilter) params.action = actionFilter;
      if (actorFilter === 'owner') params.actor_type = 'owner';
      else if (actorFilter) params.employee_id = actorFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (categoryFilter === 'conversaciones') params.category = 'conversaciones';

      const res = await axios.get(`${API_URL}/api/pos/audit-log`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        signal: controller.signal
      });
      const { rows: newRows, page: p, total: t, hasMore: hm } = res.data;
      setRows(prev => (reset ? newRows : [...prev, ...newRows]));
      setPage(p);
      setTotal(t);
      setHasMore(hm);
    } catch (e) {
      if (axios.isCancel(e) || e.code === 'ERR_CANCELED') return;
      console.error('Error auditoría:', e);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setActionFilter('');
    setActorFilter('');
    setDateFrom('');
    setDateTo('');
    setCategoryFilter('general');
  };

  const hasActiveFilters = !!(actionFilter || actorFilter || dateFrom || dateTo || categoryFilter !== 'general');

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History className="w-6 h-6 text-gray-700" /> Auditoría
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Quién anuló ventas, cambió precios o ajustó stock. Solo visible para el dueño de la cuenta.
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-4 bg-white rounded-lg shadow p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Categoría</label>
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setActionFilter(''); }}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="general">General (caja, productos, ventas...)</option>
            <option value="conversaciones">Conversaciones (WhatsApp)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Acción</label>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todas</option>
            {Object.entries(ACTION_LABELS)
              .filter(([key]) => categoryFilter === 'conversaciones' ? key === 'crm_message_sent' : key !== 'crm_message_sent')
              .map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Quién</label>
          <select value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todos</option>
            <option value="owner">Dueño</option>
            {employees.map((e) => (
              <option key={e.employee_id} value={e.employee_id}>{e.employee_name}</option>
            ))}
          </select>
        </div>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="text-sm text-blue-600 hover:underline pb-2">
            Limpiar filtros
          </button>
        )}
        <span className="text-xs text-gray-400 pb-2 ml-auto">{total} registros</span>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quién</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  <span className="flex items-center gap-1.5">
                    {r.actor_type === 'owner' ? <Shield className="w-3.5 h-3.5 text-gray-400" /> : <User className="w-3.5 h-3.5 text-gray-400" />}
                    {r.actor_type === 'owner' ? 'Dueño' : (r.employee_name || 'Empleado eliminado')}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${ACTION_COLORS[r.action] || 'bg-gray-100 text-gray-700'}`}>
                    {ACTION_LABELS[r.action] || r.action}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatDetail(r.action, r.detail)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && rows.length === 0 && (
          <div className="text-center py-12"><p className="text-gray-500">Cargando...</p></div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-center py-12">
            <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {hasActiveFilters ? 'Ningún registro coincide con los filtros' : 'Todavía no hay nada registrado'}
            </p>
          </div>
        )}
        {hasMore && (
          <div className="text-center py-4 border-t">
            <button
              onClick={() => load({ reset: false })}
              disabled={loading}
              className="px-4 py-2 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50"
            >
              {loading ? 'Cargando...' : `Cargar más (${rows.length} de ${total})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditoriaLog;
